import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { Location } from "history";
import { AxiosError } from "axios";
import queryString from "query-string";
import Modal from "../../components/Modal";
import client from "lib/CommonApi/client";
import { callAlertWeb, callAppLoginWeb, hideProgressWeb, showProgressWeb } from 'common/appInterface';

const CardApplyStopModal: React.FC<CardApplyStopProps> = (props) => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** Navigate */
  const navigate = useNavigate();
  /** 다국어처리 */
  const { t } = useTranslation();

  /** 재진입(이어하기) 후 아직 접수상태가 바뀌지 않은 신청 여부
   *    — 신분증 업로드(04→05)·추천코드 저장(05→06) 시 false 로 해제 */
  const isContinue = location.state.apply?.isContinue === true;
  /** 카드 재발급 신청 여부 — 문구는 재진입과 같은 exit 문구, "예"는 취소 없이 인트로 이동 */
  const isReissue = location.state.apply?.isReissue === true;

  /** 인트로 재진입 (네이티브 웹뷰 재진입과 동일하게 보유카드 여부로 상품목록/메인 분기) */
  const moveToIntro = useCallback(() => {
    const query: { [key: string]: any } = { ...location.state.query };

    // 메뉴 이동 파라미터는 제외 (인트로/메인에서 메뉴 이동이 다시 실행되지 않도록)
    // 단, 카드 신청 메뉴(menuCode 9)로 진입한 경우는 유지 → 인트로가 처음처럼 카드(상품) 목록으로 이동
    if (query.menuCode !== '9') {
      delete query.menuCode;
    }
    delete query.navigatePath;

    navigate("/intro?" + queryString.stringify(query));
  }, [navigate, location]);

  /** 신청중단 처리 */
  const clickStop = useCallback(async () => {
    if (isContinue) {
      // 재진입 후 상태 변경 없이 중단: 접수내역 취소 후 인트로로 재진입 (실패 시 팝업 유지)
      showProgressWeb();
      await client.patch("/api/app/apply/cancel", {
        ...location.state.query,
        receiveSeq: location.state.apply.receiveSeq,
        receiveDate: location.state.apply.receiveDate
      }).then((response) => {
        if (response.data.code === "00") {
          const apply = location.state.apply;

          // 상품상세·혜택 탭에서 재진입한 경우: 바로 앞 history 가 시작 화면이면 그 화면으로 돌아감 (아니면 인트로)
          //  - 상품상세: 다시 마운트되며 상세·접수내역 재조회
          //  - 혜택 탭: 선택한 카드 탭 복원(history state) 후 /my 재조회
          if (apply.continueFromHistoryIdx !== undefined && window.history.state?.idx - 1 === apply.continueFromHistoryIdx
                && ['productDetail', 'benefitsTab'].includes(apply.continueFrom)) {
            navigate(-1);
            return;
          }

          moveToIntro();
        } else if (response.data.code === "o4") {
          callAlertWeb("ERROR", response.data.code, response.data.message);
          callAppLoginWeb();
        } else {
          callAlertWeb("ERROR", response.data.code, response.data.message);
        }
      }).catch((e: AxiosError) => {
        callAlertWeb("ERROR", "99", e.message);
      }).finally(() => {
        hideProgressWeb();
      });
    } else {
      moveToIntro();
    }
  }, [location, isContinue, navigate, moveToIntro]);

  return (
    <Modal isOpen={props.isStop} onRequestClose={props.closeStop} contentLabel="modal" className="popTyLayer2 cardApplyStop" overlayClassName="dim" closeTimeoutMS={500}>
      <p className="message">
        { isContinue || isReissue ?
            t("APPL98.exitPopup01", "Exit without saving?") :
            t("APPL99.stopPopup05", "Cancel your card application?") }
      </p>
      <p className="subTxt mgt20">
        { isContinue || isReissue ?
            t("APPL98.exitPopup02", "Your progress will not be saved. You will need to start again from the first step.") :
            t("APPL99.stopPopup06", "The information you entered will be saved. You can continue your application later.") }
      </p>
      <div className="btnWrapTy02">
        <button type="button" className="btnPrimaryTy03" onClick={props.onConfirm ?? clickStop}>
          <span>
            { isContinue || isReissue ?
              t("APPL98.exitButton01", "Exit") :
              t("APPL99.stopButton01", "Cancel Application") }
          </span>
        </button>
        <button type="button" className="btnPrimaryTy01" onClick={props.closeStop}>
          <span>
            { isContinue || isReissue ?
                t("APPL98.exitButton02", "Continue") :
                t("APPL99.stopButton03", "Continue") }
          </span>
        </button>
      </div>
    </Modal>
  );
}

export default CardApplyStopModal;